import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '../lib/supabase'

export interface User {
  id: string
  email: string
  full_name: string
  role: 'admin' | 'manager' | 'viewer'
  is_active: boolean
  is_approved: boolean
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const originalUser = ref<User | null>(null) // Store original user when impersonating
  const isImpersonating = ref(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => user.value !== null)

  // Role-based getters
  const isAdmin = computed(() => {
    if (isImpersonating.value && originalUser.value) {
      return originalUser.value.role === 'admin'
    }
    return user.value?.role === 'admin'
  })

  const isManager = computed(() => user.value?.role === 'manager')
  const isViewer = computed(() => user.value?.role === 'viewer')

  // Permission helpers based on role
  const canView = computed(() => true) // Everyone can view
  const canCreate = computed(() => isAdmin.value || isManager.value)
  const canEdit = computed(() => isAdmin.value || isManager.value)
  const canDelete = computed(() => isAdmin.value)

  // Actions
  const login = async (email: string, password: string) => {
    isLoading.value = true
    error.value = null

    try {
      // Call custom login function
      const { data, error: loginError } = await supabase.rpc('custom_login', {
        user_email: email,
        user_password: password
      })

      if (loginError) {
        throw new Error(loginError.message)
      }

      if (!data.success) {
        throw new Error(data.error || 'Invalid email or password')
      }

      // Fetch user data
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, full_name, first_name, last_name, role, is_active, is_approved')
        .eq('id', data.user.id)
        .single()

      if (userError) {
        throw new Error(userError.message)
      }

      // Set user data
      user.value = {
        id: userData.id,
        email: userData.email,
        full_name: userData.full_name || (userData.first_name + (userData.last_name ? ' ' + userData.last_name : '')),
        role: userData.role,
        is_active: userData.is_active,
        is_approved: userData.is_approved
      }

      // Store user in localStorage
      localStorage.setItem('user', JSON.stringify(user.value))
      localStorage.setItem('isAuthenticated', 'true')

      console.log('✅ Login successful:', {
        user: user.value.email,
        role: user.value.role
      })

      return user.value
    } catch (err: any) {
      error.value = err.message || 'Login failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const register = async (fullName: string, email: string, password: string) => {
    isLoading.value = true
    error.value = null

    try {
      // Split full name into first and last name
      const nameParts = fullName.trim().split(' ')
      const firstName = nameParts[0] || ''
      const lastName = nameParts.slice(1).join(' ') || ''

      // Insert new user
      const { data, error: registerError } = await supabase
        .from('users')
        .insert([
          {
            email: email,
            password_hash: password, // Will be hashed by trigger
            full_name: fullName,
            first_name: firstName,
            last_name: lastName,
            role: 'viewer', // Default role
            is_active: true,
            is_approved: false // Needs admin approval
          }
        ])
        .select()
        .single()

      if (registerError) {
        if (registerError.code === '23505') {
          throw new Error('อีเมลนี้ถูกใช้งานแล้ว')
        }
        throw new Error(registerError.message)
      }

      return data
    } catch (err: any) {
      error.value = err.message || 'Registration failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const logout = () => {
    user.value = null
    originalUser.value = null
    isImpersonating.value = false
    localStorage.removeItem('user')
    localStorage.removeItem('isAuthenticated')
    localStorage.removeItem('isImpersonating')
    localStorage.removeItem('originalUser')
  }

  const checkAuth = async () => {
    const storedUser = localStorage.getItem('user')
    const isAuth = localStorage.getItem('isAuthenticated')
    const storedIsImpersonating = localStorage.getItem('isImpersonating')
    const storedOriginalUser = localStorage.getItem('originalUser')

    if (isAuth === 'true' && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser)
        user.value = parsedUser

        // Restore impersonating state
        if (storedIsImpersonating === 'true' && storedOriginalUser) {
          isImpersonating.value = true
          originalUser.value = JSON.parse(storedOriginalUser)
        }

        console.log('✅ Auth restored:', {
          user: user.value.email,
          role: user.value.role
        })
      } catch (err) {
        console.error('Failed to parse stored user:', err)
        logout()
      }
    }
  }

  const updateUser = async (userId: string, updates: Partial<User>) => {
    isLoading.value = true
    error.value = null

    try {
      const { data, error: updateError } = await supabase
        .from('users')
        .update(updates)
        .eq('id', userId)
        .select()
        .single()

      if (updateError) {
        throw new Error(updateError.message)
      }

      // Update current user if updating self
      if (user.value && user.value.id === userId) {
        user.value = { ...user.value, ...data }
        localStorage.setItem('user', JSON.stringify(user.value))
      }

      return data
    } catch (err: any) {
      error.value = err.message || 'Update failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const fetchUsers = async () => {
    isLoading.value = true
    error.value = null

    try {
      const { data, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false })

      if (fetchError) {
        throw new Error(fetchError.message)
      }

      return data
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch users'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const approveUser = async (userId: string) => {
    return updateUser(userId, { is_approved: true })
  }

  const deleteUser = async (userId: string) => {
    isLoading.value = true
    error.value = null

    try {
      const { error: deleteError } = await supabase
        .from('users')
        .delete()
        .eq('id', userId)

      if (deleteError) {
        throw new Error(deleteError.message)
      }
    } catch (err: any) {
      error.value = err.message || 'Delete failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Impersonate another role (Admin only)
  const impersonateRole = (role: 'admin' | 'manager' | 'viewer') => {
    if (!isAdmin.value || isImpersonating.value) {
      throw new Error('Only admins can impersonate roles')
    }

    // Store original user
    originalUser.value = { ...user.value! }
    isImpersonating.value = true

    // Change current user's role
    user.value = {
      ...user.value!,
      role: role
    }

    // Store in localStorage
    localStorage.setItem('isImpersonating', 'true')
    localStorage.setItem('originalUser', JSON.stringify(originalUser.value))
    localStorage.setItem('user', JSON.stringify(user.value))
  }

  // Stop impersonating and return to original role
  const stopImpersonating = () => {
    if (!isImpersonating.value || !originalUser.value) {
      return
    }

    // Restore original user
    user.value = { ...originalUser.value }
    originalUser.value = null
    isImpersonating.value = false

    // Update localStorage
    localStorage.removeItem('isImpersonating')
    localStorage.removeItem('originalUser')
    localStorage.setItem('user', JSON.stringify(user.value))
  }

  return {
    // State
    user,
    originalUser,
    isImpersonating,
    isLoading,
    error,
    // Getters
    isAuthenticated,
    isAdmin,
    isManager,
    isViewer,
    canView,
    canCreate,
    canEdit,
    canDelete,
    // Actions
    login,
    register,
    logout,
    checkAuth,
    updateUser,
    fetchUsers,
    approveUser,
    deleteUser,
    impersonateRole,
    stopImpersonating
  }
})
